import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  adminContentStats, adminList, adminUpdate, adminDelete, adminCreate,
  adminStartManualFromIdea, adminGenerateAIDraftFromIdea,
  adminBulkGenerate, adminBulkJob,
} from "../../lib/api";
import {
  BarChart3, Lightbulb, FileEdit, Sparkles, Trash2, Plus, Filter,
  CheckCircle2, Clock, FilePlus, Loader2, Edit2, Save, X, Layers, AlertCircle,
} from "lucide-react";
import config from "../../config/stateConfig";

const STATUS_OPTIONS = [
  { value: "queued", label: "Queued", color: "bg-yellow-100 text-yellow-700" },
  { value: "in_progress", label: "In Progress", color: "bg-blue-100 text-blue-700" },
  { value: "drafted", label: "Drafted", color: "bg-emerald-100 text-emerald-700" },
  { value: "dismissed", label: "Dismissed", color: "bg-gray-100 text-gray-600" },
];

const INTENT_OPTIONS = [
  { value: "informational", label: "Informational" },
  { value: "commercial", label: "Commercial" },
  { value: "transactional", label: "Transactional" },
  { value: "navigational", label: "Navigational" },
];

function StatCard({ icon: Icon, label, value, tone = "forest" }) {
  return (
    <div className="bg-white rounded-2xl border border-black/5 p-5" data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-9 h-9 rounded-lg grid place-items-center bg-${tone}/10 text-${tone}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="text-xs uppercase tracking-wider text-charcoal-soft font-bold">{label}</div>
      </div>
      <div className="font-display font-black text-3xl text-forest">{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const opt = STATUS_OPTIONS.find((o) => o.value === status) || STATUS_OPTIONS[0];
  return (
    <span className={`inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${opt.color}`}>
      {opt.label}
    </span>
  );
}

export default function ContentStrategy() {
  const [stats, setStats] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("queued");
  const [filterCluster, setFilterCluster] = useState("all");
  const [actingOn, setActingOn] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newIdea, setNewIdea] = useState({ title: "", cluster: "", intent: "informational", difficulty: 3, lead_priority: 3 });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkJob, setBulkJob] = useState(null); // {job_id, total} → polled
  const [bulkJobData, setBulkJobData] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [s, i] = await Promise.all([adminContentStats(), adminList("topic-ideas")]);
      setStats(s);
      setIdeas(i);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const clusters = stats ? Object.keys(stats.by_cluster).sort() : [];

  const filtered = ideas.filter((it) => {
    if (filterStatus !== "all" && it.status !== filterStatus) return false;
    if (filterCluster !== "all" && it.cluster !== filterCluster) return false;
    return true;
  });

  const startManual = async (idea) => {
    setActingOn(idea.id);
    try {
      const post = await adminStartManualFromIdea(idea.id);
      navigate("/admin/blog");
      // Inform user where the new draft is
      setTimeout(() => {
        alert(`Draft created: "${post.title}". Find it in Blog Posts (status: draft).`);
      }, 200);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to create draft");
    } finally {
      setActingOn(null);
    }
  };

  const generateAI = async (idea) => {
    if (!window.confirm(`Use AI to generate a full draft for "${idea.title}"?\n\nThis uses your Universal Key budget. The draft will land in Blog Posts under "In Review".`)) return;
    setActingOn(idea.id);
    try {
      await adminGenerateAIDraftFromIdea(idea.id);
      await load();
      alert("AI draft created and saved as 'In Review' under Blog Posts.");
    } catch (e) {
      const detail = e?.response?.data?.detail || "AI generation failed";
      alert(detail);
    } finally {
      setActingOn(null);
    }
  };

  const updateIdeaStatus = async (idea, status) => {
    await adminUpdate("topic-ideas", idea.id, { status });
    setIdeas((xs) => xs.map((x) => (x.id === idea.id ? { ...x, status } : x)));
    adminContentStats().then(setStats).catch(() => {});
  };

  const removeIdea = async (id) => {
    if (!window.confirm("Delete this topic idea?")) return;
    await adminDelete("topic-ideas", id);
    setIdeas((xs) => xs.filter((x) => x.id !== id));
    adminContentStats().then(setStats).catch(() => {});
  };

  const createIdea = async () => {
    if (!newIdea.title.trim() || !newIdea.cluster.trim()) {
      alert("Title and cluster are required.");
      return;
    }
    await adminCreate("topic-ideas", {
      ...newIdea,
      difficulty: parseInt(newIdea.difficulty) || 3,
      lead_priority: parseInt(newIdea.lead_priority) || 3,
    });
    setNewIdea({ title: "", cluster: "", intent: "informational", difficulty: 3, lead_priority: 3 });
    setShowNew(false);
    load();
  };

  const startEdit = (idea) => {
    setEditing(idea.id);
    setEditForm({ ...idea });
  };
  const saveEdit = async () => {
    await adminUpdate("topic-ideas", editing, {
      title: editForm.title,
      cluster: editForm.cluster,
      intent: editForm.intent,
      difficulty: parseInt(editForm.difficulty) || 3,
      lead_priority: parseInt(editForm.lead_priority) || 3,
      notes: editForm.notes || "",
    });
    setEditing(null);
    load();
  };

  const toggleSelect = (id) => {
    setSelectedIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllFiltered = () => {
    setSelectedIds(new Set(filtered.map((it) => it.id)));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const startBulk = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(
      `Generate ${ids.length} AI draft${ids.length === 1 ? "" : "s"} now?\n\n` +
      `Drafts land in Blog Posts under "In Review" — you still approve each one before it goes live.\n` +
      `This uses your Universal Key budget (~${ids.length * 0.05}–${(ids.length * 0.1).toFixed(2)} estimated).`
    )) return;
    try {
      const job = await adminBulkGenerate(ids);
      setBulkJob(job);
      setBulkJobData({ status: "running", total: job.total, completed: 0, results: [] });
      pollBulkJob(job.job_id);
    } catch (e) {
      alert(e?.response?.data?.detail || "Bulk start failed");
    }
  };

  const pollBulkJob = async (jobId) => {
    let active = true;
    const tick = async () => {
      if (!active) return;
      try {
        const data = await adminBulkJob(jobId);
        setBulkJobData(data);
        if (data.status === "complete") {
          active = false;
          await load();
          return;
        }
      } catch (e) { /* ignore transient */ }
      setTimeout(tick, 2500);
    };
    tick();
  };

  const closeBulk = () => {
    setBulkJob(null);
    setBulkJobData(null);
    setSelectedIds(new Set());
  };

  return (
    <div data-testid="content-strategy-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-forest">Content Strategy</h1>
          <p className="text-charcoal-soft text-sm mt-1">
            SEO topic engine. 44 curated {config.stateName} accident topics, scored by opportunity. Promote ideas to drafts manually
            (no AI required) or with optional AI assist. You stay in control of every word that ships.
          </p>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-primary text-sm" data-testid="content-strategy-new-idea">
          <Plus className="w-4 h-4" /> Add Idea
        </button>
      </div>

      {bulkJob && (
        <div className="fixed inset-0 z-50 grid place-items-center px-4 bg-black/60 backdrop-blur-sm" data-testid="bulk-job-modal">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-forest text-bone grid place-items-center">
                <Layers className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-display font-bold text-xl text-forest">Bulk AI Drafts</h3>
                <p className="text-xs text-charcoal-soft">
                  {bulkJobData?.completed || 0} / {bulkJobData?.total || bulkJob.total} complete
                  {bulkJobData?.status === "complete" ? " — finished." : "…"}
                </p>
              </div>
              {bulkJobData?.status === "complete" && (
                <button onClick={closeBulk} className="btn-ghost text-sm" data-testid="bulk-job-close">
                  <X className="w-4 h-4" /> Close
                </button>
              )}
            </div>
            <div className="h-2 bg-bone-alt rounded-full overflow-hidden mb-4">
              <div
                className="h-full bg-forest transition-all"
                style={{ width: `${Math.round(((bulkJobData?.completed || 0) / (bulkJobData?.total || 1)) * 100)}%` }}
              />
            </div>
            <div className="max-h-72 overflow-y-auto space-y-1 text-sm">
              {(bulkJobData?.results || []).map((r, i) => (
                <div key={i} className="flex items-center gap-2">
                  {r.status === "ok" ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />
                  )}
                  <span className="truncate">
                    {r.status === "ok" ? (
                      <span className="text-charcoal">{r.title || r.slug}</span>
                    ) : (
                      <span className="text-red-700">{r.error || "Failed"}</span>
                    )}
                  </span>
                </div>
              ))}
              {bulkJobData?.status !== "complete" && (
                <div className="flex items-center gap-2 text-charcoal-soft">
                  <Loader2 className="w-4 h-4 animate-spin" /> Generating next article…
                </div>
              )}
            </div>
            {bulkJobData?.status === "complete" && (
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => { closeBulk(); navigate("/admin/blog"); }}
                  className="btn-secondary text-sm"
                  data-testid="bulk-job-view-blog"
                >
                  Review drafts in Blog Posts
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {loading || !stats ? (
        <div className="text-charcoal-soft py-12 text-center">Loading…</div>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <StatCard icon={Lightbulb} label="Ideas Queued" value={stats.ideas_by_status.queued || 0} />
            <StatCard icon={FileEdit} label="Posts in Draft" value={(stats.posts_by_status.draft || 0) + (stats.posts_by_status.in_review || 0)} />
            <StatCard icon={Clock} label="Scheduled" value={stats.posts_by_status.scheduled || 0} />
            <StatCard icon={CheckCircle2} label="Published" value={stats.posts_by_status.published || 0} />
          </div>

          {/* Cluster overview */}
          <div className="bg-white rounded-2xl border border-black/5 p-5 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="w-4 h-4 text-forest" />
              <h2 className="font-display font-bold text-forest">Topic Clusters</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {clusters.map((c) => {
                const d = stats.by_cluster[c];
                return (
                  <button
                    key={c}
                    onClick={() => setFilterCluster(c)}
                    className={`text-left p-3 rounded-lg border text-sm transition ${
                      filterCluster === c ? "border-forest bg-forest/5" : "border-black/5 hover:bg-bone-alt"
                    }`}
                    data-testid={`cluster-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <div className="font-semibold text-charcoal">{c}</div>
                    <div className="text-xs text-charcoal-soft mt-0.5">
                      {d.queued} queued · {d.drafted} drafted · {d.total} total
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <Filter className="w-4 h-4 text-charcoal-soft" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="text-sm p-2 rounded-lg border border-black/10 bg-white"
              data-testid="filter-status"
            >
              <option value="all">All statuses</option>
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select
              value={filterCluster}
              onChange={(e) => setFilterCluster(e.target.value)}
              className="text-sm p-2 rounded-lg border border-black/10 bg-white"
              data-testid="filter-cluster"
            >
              <option value="all">All clusters</option>
              {clusters.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <span className="text-xs text-charcoal-soft ml-auto">{filtered.length} ideas</span>
          </div>

          {/* Bulk-select toolbar */}
          <div className="flex items-center gap-2 mb-3 flex-wrap bg-white p-2.5 rounded-xl border border-black/5" data-testid="bulk-toolbar">
            <button onClick={selectAllFiltered} className="text-xs btn-ghost px-2 py-1.5" data-testid="bulk-select-all">
              Select all filtered
            </button>
            <button onClick={clearSelection} className="text-xs btn-ghost px-2 py-1.5" data-testid="bulk-clear">
              Clear
            </button>
            <span className="text-xs text-charcoal-soft">{selectedIds.size} selected</span>
            <div className="flex-1" />
            <button
              onClick={startBulk}
              disabled={selectedIds.size === 0}
              className="btn-secondary text-xs px-3 py-2 disabled:opacity-40"
              data-testid="bulk-generate-btn"
              title="Generate AI drafts for the selected ideas. Drafts land as 'In Review' — you still approve each one."
            >
              <Layers className="w-3.5 h-3.5" /> Generate {selectedIds.size > 0 ? selectedIds.size + " " : ""}AI drafts
            </button>
          </div>

          {/* New idea form */}
          {showNew && (
            <div className="bg-white rounded-2xl border border-black/5 p-5 mb-4" data-testid="new-idea-form">
              <h3 className="font-display font-bold text-forest mb-3">New Topic Idea</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <input
                  type="text" placeholder="Topic title"
                  value={newIdea.title}
                  onChange={(e) => setNewIdea({ ...newIdea, title: e.target.value })}
                  className="p-2.5 rounded-lg border border-black/10 outline-none focus:border-forest md:col-span-2"
                  data-testid="new-idea-title"
                />
                <input
                  type="text" placeholder="Cluster (e.g., Car Accidents)"
                  value={newIdea.cluster}
                  onChange={(e) => setNewIdea({ ...newIdea, cluster: e.target.value })}
                  className="p-2.5 rounded-lg border border-black/10 outline-none focus:border-forest"
                  data-testid="new-idea-cluster"
                />
                <select
                  value={newIdea.intent}
                  onChange={(e) => setNewIdea({ ...newIdea, intent: e.target.value })}
                  className="p-2.5 rounded-lg border border-black/10 bg-white"
                  data-testid="new-idea-intent"
                >
                  {INTENT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <div>
                  <label className="text-xs text-charcoal-soft">Difficulty (1-5)</label>
                  <input type="number" min={1} max={5} value={newIdea.difficulty}
                    onChange={(e) => setNewIdea({ ...newIdea, difficulty: e.target.value })}
                    className="w-full p-2.5 rounded-lg border border-black/10" />
                </div>
                <div>
                  <label className="text-xs text-charcoal-soft">Lead priority (1-5)</label>
                  <input type="number" min={1} max={5} value={newIdea.lead_priority}
                    onChange={(e) => setNewIdea({ ...newIdea, lead_priority: e.target.value })}
                    className="w-full p-2.5 rounded-lg border border-black/10" />
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={createIdea} className="btn-secondary text-sm" data-testid="new-idea-save">
                  <Save className="w-4 h-4" /> Add Idea
                </button>
                <button onClick={() => setShowNew(false)} className="btn-ghost text-sm">
                  <X className="w-4 h-4" /> Cancel
                </button>
              </div>
            </div>
          )}

          {/* Ideas list */}
          <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
            {filtered.length === 0 ? (
              <div className="p-10 text-center text-charcoal-soft">No ideas match the filters.</div>
            ) : (
              filtered.map((idea, i) => (
                <div key={idea.id} className={`p-4 md:p-5 ${i > 0 ? "border-t border-black/5" : ""}`} data-testid={`idea-row-${idea.id}`}>
                  {editing === idea.id ? (
                    <div className="space-y-2">
                      <input
                        type="text" value={editForm.title || ""}
                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        className="w-full p-2 rounded-lg border border-black/10 font-semibold"
                      />
                      <div className="grid md:grid-cols-4 gap-2">
                        <input type="text" placeholder="Cluster" value={editForm.cluster || ""}
                          onChange={(e) => setEditForm({ ...editForm, cluster: e.target.value })}
                          className="p-2 rounded-lg border border-black/10" />
                        <select value={editForm.intent} onChange={(e) => setEditForm({ ...editForm, intent: e.target.value })}
                          className="p-2 rounded-lg border border-black/10 bg-white">
                          {INTENT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <input type="number" min={1} max={5} value={editForm.difficulty}
                          onChange={(e) => setEditForm({ ...editForm, difficulty: e.target.value })}
                          className="p-2 rounded-lg border border-black/10" placeholder="Difficulty 1-5" />
                        <input type="number" min={1} max={5} value={editForm.lead_priority}
                          onChange={(e) => setEditForm({ ...editForm, lead_priority: e.target.value })}
                          className="p-2 rounded-lg border border-black/10" placeholder="Priority 1-5" />
                      </div>
                      <textarea
                        placeholder="Editor notes (optional)" value={editForm.notes || ""}
                        onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                        rows={2}
                        className="w-full p-2 rounded-lg border border-black/10 text-sm"
                      />
                      <div className="flex gap-2">
                        <button onClick={saveEdit} className="btn-secondary text-sm"><Save className="w-4 h-4" /> Save</button>
                        <button onClick={() => setEditing(null)} className="btn-ghost text-sm"><X className="w-4 h-4" /> Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(idea.id)}
                        onChange={() => toggleSelect(idea.id)}
                        className="w-4 h-4 accent-forest shrink-0"
                        data-testid={`idea-checkbox-${idea.id}`}
                        aria-label="Select for bulk action"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-forest">{idea.title}</div>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <StatusBadge status={idea.status} />
                          <span className="text-[11px] text-charcoal-soft">{idea.cluster}</span>
                          <span className="text-[11px] text-charcoal-soft">· {idea.intent}</span>
                          <span className="text-[11px] text-charcoal-soft">· Difficulty {idea.difficulty}/5</span>
                          <span className="text-[11px] text-charcoal-soft">· Priority {idea.lead_priority}/5</span>
                        </div>
                        {idea.blog_post_id && (
                          <div className="text-[11px] text-emerald-700 mt-1">
                            Linked to a blog draft — find it in Blog Posts.
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <select
                          value={idea.status}
                          onChange={(e) => updateIdeaStatus(idea, e.target.value)}
                          className="text-xs p-2 rounded-lg border border-black/10 bg-white"
                          data-testid={`idea-status-${idea.id}`}
                        >
                          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <button
                          onClick={() => startManual(idea)}
                          disabled={actingOn === idea.id}
                          className="btn-secondary text-xs px-3 py-2"
                          data-testid={`idea-manual-${idea.id}`}
                          title="Create a blank draft you'll write or paste manually. No AI used."
                        >
                          {actingOn === idea.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FilePlus className="w-3.5 h-3.5" />}
                          Manual draft
                        </button>
                        <button
                          onClick={() => generateAI(idea)}
                          disabled={actingOn === idea.id}
                          className="btn-ghost text-xs px-3 py-2"
                          data-testid={`idea-ai-${idea.id}`}
                          title="Optional. Uses Universal Key. Always produces 'In Review' for you to approve."
                        >
                          {actingOn === idea.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                          AI draft
                        </button>
                        <button
                          onClick={() => startEdit(idea)}
                          className="btn-ghost text-xs px-3 py-2"
                          data-testid={`idea-edit-${idea.id}`}
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => removeIdea(idea.id)}
                          className="text-red-500 hover:bg-red-50 p-2 rounded-lg"
                          data-testid={`idea-delete-${idea.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
